import AbstractListView from '@UI/Store/AbstractListView';
import type BeneficiaryListItemView from '@UI/Store/Beneficiaries/Views/BeneficiaryListItemView';

/**
 * Immutable View: `readonly` properties, frozen at the end of the constructor.
 * The list is filtered server-side and runs with `cache: CACHE_DISABLED` (no TTL, `deps`-driven
 * refetch): `queryKey` is the query string that produced this page, and `stopOn` compares it against
 * the current filters to decide whether the cached page is still valid.
 */
class BeneficiariesListView extends AbstractListView<BeneficiaryListItemView> {
	readonly accountId: number;
	readonly totalRecords: number;
	readonly queryKey: string;

	constructor(
		list: readonly BeneficiaryListItemView[],
		accountId: number,
		totalRecords: number,
		queryKey: string,
	) {
		super(list, 'BeneficiariesListView');

		this.accountId = accountId;
		this.totalRecords = totalRecords;
		this.queryKey = queryKey;

		Object.freeze(this);
	}

	updateBeneficiary(beneficiary: BeneficiaryListItemView): BeneficiariesListView {
		return new BeneficiariesListView(
			this.list.map((entry) => (entry.id === beneficiary.id ? beneficiary : entry)),
			this.accountId,
			this.totalRecords,
			this.queryKey,
		);
	}

	removeBeneficiary(beneficiaryId: number): BeneficiariesListView {
		if (!this.list.some((entry) => entry.id === beneficiaryId)) {
			return this;
		}

		return new BeneficiariesListView(
			this.list.filter((entry) => entry.id !== beneficiaryId),
			this.accountId,
			Math.max(0, this.totalRecords - 1),
			this.queryKey,
		);
	}

	// A `beneficiaryId` absent from the page is a no-op: `transform` never runs, the list comes back
	// unchanged.
	applyToBeneficiary(
		beneficiaryId: number,
		transform: (beneficiary: BeneficiaryListItemView) => BeneficiaryListItemView,
	): BeneficiariesListView {
		return new BeneficiariesListView(
			this.list.map((entry) => (entry.id === beneficiaryId ? transform(entry) : entry)),
			this.accountId,
			this.totalRecords,
			this.queryKey,
		);
	}
}

export default BeneficiariesListView;
